import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	AddRedressInformation,
	DocumentNameHandler,
	PageHandler,
	RolesHandler,
	SummaryHandler,
	ZoomHandler,
} from "./components";
import styles from "./PdfHeader.module.css";
import { SegFlags, environmentSwitch, isSegFeatureEnabled } from "../../../../../../common/providers";
import { Options } from "../../../main-header/components";
import LockHandler from "./components/lock-handler/LockHandler";
import { DOCUMENT_STATUSES } from "../../../../constants/constants";
import ReorderRedressLayer from "../layers/reorder-redress-layer/ReorderRedressLayer";
import { CustomIconButton, icon } from "../../../../../../common/components";
import { useApi } from "../../../../../../common/hooks";
import { DocumentService } from "../../../../../../api";
import { setPageDimensions } from "../../../../slice/document/document-slice";

type Precedence = {
	count: number;
	[key: string]: unknown;
};
type PageDimension = {
	width: number;
	height: number;
	top: number;
	page: number;
};

const PdfHeader: React.FC<{ openDrawer: boolean }> = ({ openDrawer }) => {
	const { call: updatePageRotation } = useApi(DocumentService.updatePageRotation);
	const precedences: Precedence[] = useSelector(({ srProjectDocument }) => srProjectDocument.documents.precedence);
	const projectId = useSelector(({ context }) => context?.project?.id);
	const page = useSelector(({ srPdf }) => srPdf.page);
	const docId = useSelector(({ srDocument }) => srDocument.documentId);
	const pageDimensions = useSelector(({ srDocument }) => srDocument.pageDimensions);
	const dispatch = useDispatch();
	const isThereAnyDocument: boolean = useMemo(
		() => precedences.some((precedence) => precedence.count > 0),
		[precedences]
	);
	const addRedressmentFocusActive = useSelector(({ srPdf }) => srPdf.addRedressmentFocusActive);
	const displayRoles = isSegFeatureEnabled(SegFlags.RIGHTS_ON_DOCUMENTS) && !addRedressmentFocusActive;
	const isArchived: boolean = useSelector(({ srDocument }) => srDocument.status === DOCUMENT_STATUSES.ARCHIVED);
	const handleRotateDocumentPage = () => {
		const currentRotation = pageDimensions.find((pd: PageDimension) => pd.page === page + 1)?.rotation || 0;
		updatePageRotation(
			{ projectId, docId },
			{ page: page + 1, rotation: currentRotation === 270 ? 0 : currentRotation + 90 }
		)
			.then(() => {
				dispatch(
					setPageDimensions(
						pageDimensions.map((pd: PageDimension) =>
							pd.page === page + 1
								? { ...pd, rotation: currentRotation === 270 ? 0 : currentRotation + 90 }
								: pd
						)
					)
				);
			})
			.catch(console.error);
	};
	return (
		<div className={styles.container} data-display-roles={displayRoles && !isArchived}>
			{displayRoles && !isArchived && (
				<>
					<RolesHandler />
					{isThereAnyDocument && <LockHandler />}
				</>
			)}
			<DocumentNameHandler />
			{isSegFeatureEnabled(SegFlags.AI_DOCUMENT_SUMMARY) && !isArchived && <SummaryHandler />}
			{environmentSwitch({
				veolia: isThereAnyDocument && <Options />,
				main: isThereAnyDocument && <Options />,
				smac: null,
			})}
			<ZoomHandler openDrawer={openDrawer} />
			<PageHandler />
			<CustomIconButton
				icon={icon.faRotateRight}
				size="sm"
				variant="outlined"
				onClick={handleRotateDocumentPage}
			/>
			{addRedressmentFocusActive && <AddRedressInformation />}
			<ReorderRedressLayer />
		</div>
	);
};
export default PdfHeader;
